/**
 * [NEW SALES BREAKDOWN 2026-09-17, user] The hover list for a Refunds / Disputes or Disputes Won
 * box: every row behind the figure (website · person · type · amount) and an exact Total.
 *
 * This is My Inventory's KpiBreakdownCard (InventoryPage, [MYINV-KPI-BREAKDOWN 2026-07-07]) —
 * same look, size, glass surface, placement and scroll behaviour — lifted into a component so the
 * New Sales tab can use it "just like the My Inventory tab". My Inventory and My Sales keep their
 * own in-page copies untouched.
 *
 * Behaviour (identical to My Inventory):
 *   • Portaled to <body> so a frozen strip's overflow cannot clip it.
 *   • Placed below the box, centred and clamped to the viewport; flips above near the bottom.
 *   • Renders EVERY row; the list scrolls internally past ~7 rows.
 *   • Interactive: the page delays the hide briefly so the cursor can travel onto the card, and
 *     the card's onEnter cancels that hide (see useRefundBreakdownHover).
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { GLASS_BLUR, GLASS_RADIUS, glassBackground, glassBorder, glassInk, glassShadow } from './TipGlass';
import { pageRect, pageViewport } from '../utils/appZoom'; // [APP-FIT 2026-10-26] popups placed in page px

export interface RefundBreakdownItem {
  website: string;
  person: string;           // '' = unassigned
  amount: number;
  type: 'Refund' | 'Dispute';
}

export interface RefundBreakdownTip {
  anchor: DOMRect;
  items: RefundBreakdownItem[];
  tone: 'red' | 'green';
  showType: boolean;        // Refunds / Disputes shows "· Refund" / "· Dispute"; Disputes Won hides it
  countLabel: string;
}

const Box = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 6000;
  pointer-events: auto;
  min-width: 244px;
  max-width: 320px;
  padding: 11px 14px;
  text-align: left;
  border-radius: ${GLASS_RADIUS}px;
  color: ${(p) => glassInk(p.theme.colorScheme === 'dark')};
  background: ${(p) => glassBackground(p.theme.colorScheme === 'dark')};
  backdrop-filter: ${GLASS_BLUR};
  -webkit-backdrop-filter: ${GLASS_BLUR};
  border: ${(p) => glassBorder(p.theme.colorScheme === 'dark')};
  box-shadow: ${(p) => glassShadow(p.theme.colorScheme === 'dark')};
`;

const Header = styled.div`
  font-size: 9.5px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  color: ${(p) => p.theme.muted};
  margin-bottom: 9px;
  padding-bottom: 7px;
  border-bottom: 1px solid ${(p) => p.theme.line};
`;

const Item = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  & + & { margin-top: 8px; }
`;

const ItemLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const Site = styled.span`
  font-size: 12.5px;
  font-weight: 600;
  color: ${(p) => p.theme.text};
  max-width: 190px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Sub = styled.span`
  font-size: 9.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: ${(p) => p.theme.muted};
  max-width: 190px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Amt = styled.span<{ $tone: 'red' | 'green'; $strong?: boolean }>`
  flex: none;
  font-size: 13px;
  font-weight: ${(p) => (p.$strong ? 800 : 700)};
  font-variant-numeric: tabular-nums;
  color: ${(p) =>
    p.$tone === 'green'
      ? (p.theme.colorScheme === 'dark' ? '#4acd8b' : '#1a9f7d')
      : (p.theme.colorScheme === 'dark' ? '#f87171' : '#dc2626')};
`;

/* ~7 rows visible (a sliver of the 8th hints "scroll for more"); the rest scroll. */
const List = styled.div`
  max-height: min(50vh / var(--app-zoom, 1), 256px); /* [APP-FIT 2026-10-26] the window in page px */
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  margin-right: -6px;
  padding-right: 6px;
  scrollbar-width: thin;
  scrollbar-color: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.28)' : 'rgba(22,25,23,0.22)')} transparent;
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.24)' : 'rgba(22,25,23,0.20)')};
    border-radius: 3px;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: ${(p) => p.theme.line};
  margin: 9px 0 8px;
`;

const TotalRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
`;

const TotalLabel = styled.span`
  font-size: 9.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: ${(p) => p.theme.muted};
`;

export const RefundBreakdownCard: React.FC<{
  tip: RefundBreakdownTip | null;
  format: (n: number) => string;
  onEnter: () => void;
  onLeave: () => void;
}> = ({ tip, format, onEnter, onLeave }) => {
  const boxRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!tip || !box) return;
    // [APP-FIT 2026-10-26] In page px (utils/appZoom): the left / top set below are page px under the app zoom.
    const { width: bw, height: bh } = pageRect(box);
    const { width: vw, height: vh } = pageViewport();
    const anchor = pageRect(tip.anchor);
    const GAP = 8;
    const MARGIN = 8;
    let left = anchor.left + anchor.width / 2 - bw / 2;
    left = Math.max(MARGIN, Math.min(left, vw - bw - MARGIN));
    let top = anchor.bottom + GAP;
    if (top + bh > vh - MARGIN) {
      const above = anchor.top - GAP - bh;
      top = above >= MARGIN ? above : Math.max(MARGIN, Math.min(top, vh - bh - MARGIN));
    }
    box.style.left = `${Math.round(left)}px`;
    box.style.top = `${Math.round(top)}px`;
  }, [tip]);
  if (!tip) return null;
  const total = tip.items.reduce((s, it) => s + (Number(it.amount) || 0), 0);
  return createPortal(
    <Box ref={boxRef} onMouseEnter={onEnter} onMouseLeave={onLeave}>
      <Header>{tip.countLabel}</Header>
      <List>
        {tip.items.map((it, i) => (
          <Item key={`${it.website}-${it.person}-${i}`}>
            <ItemLeft>
              <Site>{it.website || '—'}</Site>
              <Sub>{it.person || 'Unassigned'}{tip.showType ? ` · ${it.type}` : ''}</Sub>
            </ItemLeft>
            <Amt $tone={tip.tone}>{format(it.amount)}</Amt>
          </Item>
        ))}
      </List>
      <Divider />
      <TotalRow>
        <TotalLabel>Total</TotalLabel>
        <Amt $tone={tip.tone} $strong>{format(total)}</Amt>
      </TotalRow>
    </Box>,
    document.body,
  );
};

/**
 * The hover timing My Inventory uses: a 600 ms dwell before the card opens, and a 160 ms delayed
 * hide on leave so the cursor can cross the gap onto the (scrollable) card. A box with no rows
 * opens nothing.
 */
export function useRefundBreakdownHover() {
  const [tip, setTip] = useState<RefundBreakdownTip | null>(null);
  const showTimer = useRef<number | null>(null);
  const hideTimer = useRef<number | null>(null);
  const cancelHide = useCallback(() => {
    if (hideTimer.current != null) { window.clearTimeout(hideTimer.current); hideTimer.current = null; }
  }, []);
  useEffect(() => () => {
    if (showTimer.current != null) window.clearTimeout(showTimer.current);
    if (hideTimer.current != null) window.clearTimeout(hideTimer.current);
  }, []);
  const bind = useCallback((build: () => Omit<RefundBreakdownTip, 'anchor'> | null) => ({
    onMouseEnter: (e: React.MouseEvent<HTMLElement>) => {
      const content = build();
      if (!content || content.items.length === 0) return;
      const anchor = e.currentTarget.getBoundingClientRect();
      cancelHide();
      if (showTimer.current != null) window.clearTimeout(showTimer.current);
      showTimer.current = window.setTimeout(() => setTip({ ...content, anchor }), 600);
    },
    onMouseLeave: () => {
      if (showTimer.current != null) { window.clearTimeout(showTimer.current); showTimer.current = null; }
      cancelHide();
      hideTimer.current = window.setTimeout(() => setTip(null), 160);
    },
  }), [cancelHide]);
  return { tip, bind, cancelHide, close: () => setTip(null) };
}

export default RefundBreakdownCard;
